/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useRef, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useCommerceAPI} from '../../commerce-api/contexts'
import {TILE_CONTEXT_ACCESSORIES, TILE_CONTEXT_FANREEL_RELATED_PRODUCTS} from '../../constants'

// Components
import {
    Flex,
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    Stack,
    HStack,
    Button,
    Center,
    useMultiStyleConfig
} from '@chakra-ui/react'

import DynamicImage from '../dynamic-image'
import SwatchGroup from '../../components/swatch-group'
import Swatch from '../../components/swatch-group/swatch'
import {AddIcon} from '@chakra-ui/icons'
import DisplayPrice from '../../components/display-price'
import {trackProductTileClick, trackFanreelClickProduct} from '../../tracking/analytics'
import BadgeGroup from '../badge-group'
import HybridLink from '../hybrid-link'
import {ProductQuickviewModal, useProductQuickviewModal} from '../product-quickview-modal'

// Hooks
import {useIntl} from 'react-intl'
import useAsync from '../../commerce-api/hooks/useAsync'

// Other
import {productUrlBuilder} from '../../utils/url'
import {useColorVariation} from '../../hooks'
import {useProductDisplayImages} from '../../hooks/use-product-display-images'
import {HYBRID_PDP, PRODUCT_TILE_IMAGE_TYPE} from '../../constants'
import {PlusRoundIcon, CheckCircleIcon} from '../icons'
import useIntersectionObserver from '../../hooks/use-intersection-observer'
import BVProductRating from '../bazaarvoice/rating'
import {isProductSet} from '../../utils/product-utils'
import {hasBVProductRating} from '../bazaarvoice/utils'
import useEffectEvent from '../../hooks/use-effect-event'

// Component Skeleton
export const Skeleton = (props) => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box data-testid="sf-product-tile-skeleton" {...props}>
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <ChakraSkeleton width="50%" height="20px" />
                <ChakraSkeleton width="100%" height="12px" />
            </Stack>
        </Box>
    )
}

/**
 * The ProductTile is a visual representation of a product object that has the following variants:
 *  - The [Default] variant will show it's images, name, price and color variations and
 *    has the functionality of updating ProductTile's data by selecting variation type.
 *
 *  - The [Basic] variant will show it's images and name
 *
 *  - The [Extended] variant will show it's images, name and price
 *
 *  - The [Quickshop] variant will show it's images, name, price and will have additional button to open quickview overlay
 *
 *  - The [plp-sliced] variant is based on the 'Default' variant but shows only
 *    the color swatch (if there is any) of the current variant
 *
 *  - The [plp-unsliced] variant is based on the 'Default' variant
 *    shows multiple swatches and a “+ number of not displayed swatches”
 */
const ProductTile = (props) => {
    const {
        dynamicImageProps,
        variant,
        hideBadges,
        context,
        promotions,
        onRecommendedProductClick,
        ...rest
    } = props
    const api = useCommerceAPI()
    const intl = useIntl()
    const colorVariationsRef = useRef()
    const isMounted = useRef(true)
    const [product, setProduct] = useState(props.product)
    const [addedToCart, setAddedToCart] = useState(false)
    const [hideOverlay, setHideOverlay] = useState(false)
    const getProductProps = useAsync(api.shopperProducts.getProduct)
    const quickViewModalProps = useProductQuickviewModal()
    const Icon = addedToCart ? CheckCircleIcon : PlusRoundIcon
    const {name, productName, variants, variationValues} = product
    const styles = useMultiStyleConfig('ProductTile', {variant})

    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])

    useEffect(() => {
        // If the product is of type master there is no variationValues key in the object
        // In such case we need to create it.
        let varVals = props.product.variationValues
        if (!varVals) {
            varVals = createVariationValuesObject(props.product.variationAttributes)
        }
        setProduct({
            ...props.product,
            variationValues: varVals
        })
    }, [props.product])

    const createVariationValuesObject = (variationAttributes) => {
        if (!variationAttributes) {
            return
        }

        let resultObj = {}

        for (const vA of variationAttributes) {
            const key = vA.id
            const firstOrderable = vA.values?.find(({orderable}) => orderable) || vA.values?.[0]
            if (key && firstOrderable) {
                resultObj[key] = firstOrderable.value
            }
        }

        return resultObj
    }

    const {loading: productLoading, data: productData, error: productError} = getProductProps
    useEffect(() => {
        if (!productLoading && !productError && productData) {
            setProduct(productData)
        }
    }, [productLoading, productData, productError])

    /* Handle unmounted component */
    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    /* We use localized id and name because getProducts & getProduct returning different responses */
    const localizedProductName = name || productName

    /**
     * Filter [imageGroups] by PRODUCT_TILE_IMAGE_TYPE and selected color variation.
     * Slice them because we need only first and second image
     */
    const images = useProductDisplayImages(product, PRODUCT_TILE_IMAGE_TYPE, variationValues, 2)

    /* Initialize [mainImage] with the first image of current color variation */
    const [mainImage, setMainImage] = useState(images && images[0])

    useEffect(() => {
        setMainImage(images && images[0])
    }, [variationValues, images])

    /* Get extended variation attributes data with images etc. */
    const colorVariations = useColorVariation(product)?.values

    const changeColorVariation = (value) => {
        const {productId, id} = variants?.find((variant) => variant.variationValues.color === value)

        getProductProps.execute({
            parameters: {
                id: productId || id,
                allImages: true
            }
        })
    }

    const [numberOfSwatches, setNumberOfSwatches] = useState(0)

    const handleResize = () => {
        if (colorVariationsRef.current) {
            const {clientWidth, clientHeight} = colorVariationsRef.current
            /* Subtract original clientWidth because of paddings between swatches */
            setNumberOfSwatches((clientWidth - (clientWidth / clientHeight) * 3) / clientHeight)
        }
    }

    /* When variations are longer than [ProductTile (width)] show plus button and hide last (n) variations */
    const visibleColorVariations = useMemo(
        () =>
            colorVariations?.length > numberOfSwatches && numberOfSwatches >= 1
                ? /* Subtract by 1 because we show plus button instead last color variation when overflowing */
                  colorVariations.slice(0, Math.floor(numberOfSwatches - 1))
                : colorVariations,
        [colorVariations, numberOfSwatches]
    )

    const isColorVariationOnScreen = useIntersectionObserver(colorVariationsRef, {
        useOnce: true
    })

    useEffect(() => {
        if (colorVariationsRef.current) {
            handleResize()

            /* Attach resize event listener to change [visibleColorVariations] dynamically when the stack of variations changes */
            window.addEventListener('resize', handleResize)

            /* Cleanup event listener */
            return () => window.removeEventListener('resize', handleResize)
        }
    }, [variationValues, isColorVariationOnScreen])

    const onAddedToCart = useEffectEvent(() => {
        quickViewModalProps.onClose()
    })
    useEffect(() => {
        if (addedToCart) {
            const timeout = setTimeout(() => {
                setAddedToCart(false)
            }, 3000)
            onAddedToCart()

            return () => clearTimeout(timeout)
        }
    }, [addedToCart, onAddedToCart])
    return (
        <HybridLink
            data-testid="product-tile"
            page={HYBRID_PDP}
            to={productUrlBuilder(product, intl.locale)}
            hybridHref={product.slugUrl || product.c_slugUrl}
            {...rest}
            variant="image"
            cursor="pointer"
            role="button"
            tabIndex="0"
            {...styles.hybridlink}
            onClick={(e) => {
                switch (context) {
                    case TILE_CONTEXT_ACCESSORIES:
                        trackProductTileClick(context, product.id)
                        break
                    case TILE_CONTEXT_FANREEL_RELATED_PRODUCTS:
                        trackFanreelClickProduct(product.id)
                        break
                    default:
                        break
                }

                if (onRecommendedProductClick && !isProductSet(product)) {
                    e.preventDefault()
                    onRecommendedProductClick(product.id, context)
                } else if (quickViewModalProps?.onOpen()) {
                    e.preventDefault()
                    // the product doesn't have variation options so load the product directly
                    quickViewModalProps.onOpen(product.id, context)
                }
            }}
            {...(images?.length > 1 && {
                onMouseEnter: (e) => {
                    e.preventDefault()
                    images[1] && setMainImage(images[1])
                },
                onMouseLeave: (e) => {
                    e.preventDefault()
                    images[1] && setMainImage(images[0])
                }
            })}
        >
            {variant === 'Quickshop' && (
                <ProductQuickviewModal
                    {...quickViewModalProps}
                    onAfterAddToCart={() => {
                        setAddedToCart(true)
                    }}
                    showAddToCartModal={false}
                    scrollToTop={false}
                />
            )}
            <Box
                {...styles.container}
                onMouseOver={() => {
                    setHideOverlay(true)
                }}
                onMouseOut={() => {
                    setHideOverlay(false)
                }}
            >
                {/* Image */}
                {product && images && mainImage && (
                    <Box position="relative">
                        <Flex
                            position="absolute"
                            zIndex="1"
                            width="100%"
                            height="100%"
                            justify="end"
                            align="start"
                        >
                            {!hideBadges && (
                                <BadgeGroup
                                    product={product}
                                    max={2}
                                    maxSummedChars={18}
                                    promotions={promotions}
                                />
                            )}
                        </Flex>

                        <AspectRatio ratio={7 / 8}>
                            <Box>
                                {/* grey overlay over image */}
                                {variant.includes('plp-') && !hideOverlay && (
                                    <Box role="button" {...styles.overlay}></Box>
                                )}

                                <DynamicImage
                                    src={`${
                                        mainImage.disBaseLink || mainImage.link
                                    }[?sw={width}&q=60]`}
                                    widths={dynamicImageProps?.widths}
                                    imageProps={{
                                        alt: mainImage.alt,
                                        loading: 'lazy',
                                        objectFit: 'contain',
                                        ...dynamicImageProps?.imageProps
                                    }}
                                />
                            </Box>
                        </AspectRatio>
                    </Box>
                )}

                <Stack spacing={3} pt={4}>
                    {/* Ratings */}
                    {['Default', 'plp-sliced', 'plp-unsliced'].includes(variant) && (
                        <Box {...styles.ratings}>
                            {hasProductRating && (
                                <BVProductRating product={product} variant="productTile" />
                            )}
                        </Box>
                    )}

                    {/* Title */}
                    <Text {...styles.title}>{localizedProductName}</Text>

                    {/* Price */}
                    {variant !== 'Basic' && (
                        <Box position="relative">
                            <DisplayPrice
                                variant="tile"
                                size="sm"
                                product={product}
                                data-testid="product-tile-price"
                            />
                            {variant === 'Quickshop' && (
                                <Icon boxSize={8} {...styles.quickshopButton} />
                            )}
                        </Box>
                    )}

                    {/* Color Variations */}
                    {['Default', 'plp-sliced', 'plp-unsliced'].includes(variant) && (
                        <Box {...styles.variations}>
                            {colorVariations && visibleColorVariations && (
                                <Box ref={colorVariationsRef}>
                                    <HStack spacing={1}>
                                        <SwatchGroup
                                            onChange={(value) => {
                                                changeColorVariation(value)
                                            }}
                                            value={variationValues?.color}
                                            showLabel={false}
                                            spacing={1}
                                            size="sm"
                                        >
                                            {visibleColorVariations?.map(
                                                ({href, name, image, value, orderable}) => (
                                                    <Swatch
                                                        key={value}
                                                        href={href}
                                                        disabled={
                                                            variant === 'plp-sliced'
                                                                ? !product.orderable
                                                                : !orderable
                                                        }
                                                        value={value}
                                                        name={name}
                                                        size="sm"
                                                        propagateDisabled={props.propagateDisabled}
                                                        tooltipPlacement="top"
                                                    >
                                                        {image ? (
                                                            <Box
                                                                height="100%"
                                                                width="100%"
                                                                borderRadius="full"
                                                                backgroundRepeat="no-repeat"
                                                                backgroundSize="cover"
                                                                backgroundColor={name.toLowerCase()}
                                                                backgroundImage={
                                                                    image
                                                                        ? `url("${encodeURI(
                                                                              image.disBaseLink ||
                                                                                  image.link
                                                                          )}")`
                                                                        : ''
                                                                }
                                                            />
                                                        ) : (
                                                            name.charAt(0)
                                                        )}
                                                    </Swatch>
                                                )
                                            )}
                                        </SwatchGroup>

                                        {visibleColorVariations.length < colorVariations.length && (
                                            <Button {...styles.plusOrPlpMoreColorsButton}>
                                                <Center>
                                                    {variant === 'Default' ? (
                                                        <AddIcon w={3.5} h={3.5} />
                                                    ) : (
                                                        <Text {...styles.plpMoreColorsTxt}>
                                                            {`+${
                                                                colorVariations.length -
                                                                visibleColorVariations.length
                                                            }`}
                                                        </Text>
                                                    )}
                                                </Center>
                                            </Button>
                                        )}
                                    </HStack>
                                </Box>
                            )}
                        </Box>
                    )}
                </Stack>
            </Box>
        </HybridLink>
    )
}

ProductTile.displayName = 'ProductTile'

ProductTile.defaultProps = {
    variant: 'Default'
}

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        price: PropTypes.number,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        id: PropTypes.string,
        productId: PropTypes.string,

        // Example of variationAttributes array:
        // [
        //     {
        //         id: 'color',
        //         name: 'Farbe',
        //         values: [
        //             {name: 'Deep Grey White Chassis', orderable: false, value: '144'},
        //             {name: 'Grey Clouds', orderable: true, value: '386'}
        //         ]
        //     }
        // ]
        variationAttributes: PropTypes.array,

        // Example of variationValues object:
        // {
        //     color: '110'
        // }
        variationValues: PropTypes.object
    }),
    dynamicImageProps: PropTypes.object,
    variant: PropTypes.string,
    // Indicates weater to add the disabled attribute
    // to the inactive color swatches
    propagateDisabled: PropTypes.bool,
    promotions: PropTypes.array,
    hideBadges: PropTypes.bool,
    context: PropTypes.string,
    onRecommendedProductClick: PropTypes.func
}

export default ProductTile
